import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Download, FileSpreadsheet, FileText, Settings2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { OptionItem, QuestionType, AnswerExportFormat } from "@/types/survey";

// ============================================
// Export Helpers (Phase E - Option ID Migration)
// ============================================

/**
 * Converts option ID to display text.
 * Falls back to ID if option not found.
 */
function resolveOptionLabel(
  optionId: string, 
  options?: OptionItem[]
): string {
  if (!options) return optionId;
  const option = options.find(o => o.id === optionId);
  return option?.text || optionId;
}

/**
 * Formats answer for export, resolving option IDs to text.
 * Used for client-side export formatting.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function formatAnswerForExport(
  answer: unknown,
  question: QuestionType
): string {
  if (answer === null || answer === undefined) return '';
  
  // Single choice (string ID)
  if (typeof answer === 'string' && question.options) {
    return resolveOptionLabel(answer, question.options);
  }
  
  // Multiple choice (array of IDs)
  if (Array.isArray(answer) && question.options) {
    return answer
      .map(id => resolveOptionLabel(String(id), question.options))
      .join(', ');
  }
  
  // Other answer types
  return String(answer);
}

// ============================================
// Export Options Types
// ============================================

// Export options type - extensible for future options
export interface ExportOptions {
  format: "csv" | "xlsx";
  matrixFormat: "compact" | "expanded";
  includeMetadata: boolean;
  includeTimestamps: boolean;
  sortOrder: "newest" | "oldest";
  answerFormat: AnswerExportFormat;
}

interface ExportOptionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onExport: (options: ExportOptions) => void;
  surveyTitle?: string;
}

export function ExportOptionsDialog({
  open,
  onOpenChange,
  onExport,
  surveyTitle,
}: ExportOptionsDialogProps) {
  const [options, setOptions] = useState<ExportOptions>({
    format: "csv",
    matrixFormat: "compact",
    includeMetadata: false,
    includeTimestamps: true,
    sortOrder: "newest",
    answerFormat: "text_only",
  });

  const handleExport = () => {
    onExport(options);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-5 w-5 text-green-600 dark:text-green-400" />
            Export Responses
          </DialogTitle>
          <DialogDescription>
            {surveyTitle
              ? `Configure export settings for "${surveyTitle}"`
              : "Choose your export format and options"}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6">
          <div className="space-y-6 py-4">
          {/* Format Selection */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Export Format</Label>
            <RadioGroup
              value={options.format}
              onValueChange={(value) =>
                setOptions({ ...options, format: value as "csv" | "xlsx" })
              }
              className="grid grid-cols-2 gap-3"
            >
              <Label
                htmlFor="csv"
                className={`
                  flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all
                  ${options.format === "csv" 
                    ? "border-primary bg-primary/5" 
                    : "border-border hover:border-primary/50"
                  }
                `}
              >
                <RadioGroupItem value="csv" id="csv" className="sr-only" />
                <FileText className="h-5 w-5 text-green-600" />
                <div>
                  <p className="font-medium">CSV</p>
                  <p className="text-xs text-muted-foreground">Universal format</p>
                </div>
              </Label>
              
              <Label
                htmlFor="xlsx"
                className="flex items-center gap-3 p-4 rounded-lg border-2 border-border bg-muted cursor-not-allowed opacity-60"
              >
                <RadioGroupItem value="xlsx" id="xlsx" disabled className="sr-only" />
                <FileSpreadsheet className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="font-medium text-muted-foreground">Excel</p>
                  <p className="text-xs text-muted-foreground/70">Coming soon</p>
                </div>
              </Label>
            </RadioGroup>
          </div>

          {/* Matrix Format Selection */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Matrix Question Format</Label>
            <RadioGroup
              value={options.matrixFormat}
              onValueChange={(value) =>
                setOptions({ ...options, matrixFormat: value as "compact" | "expanded" })
              }
              className="space-y-2"
            >
              <Label
                htmlFor="compact"
                className={`
                  flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all
                  ${options.matrixFormat === "compact" 
                    ? "border-primary bg-primary/5" 
                    : "border-border hover:border-primary/50"
                  }
                `}
              >
                <RadioGroupItem value="compact" id="compact" className="sr-only" />
                <div className="flex-1">
                  <p className="font-medium text-sm">Compact</p>
                  <p className="text-xs text-muted-foreground">
                    One column per question. Matrix answers in single cell.
                  </p>
                </div>
              </Label>
              
              <Label
                htmlFor="expanded"
                className={`
                  flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all
                  ${options.matrixFormat === "expanded" 
                    ? "border-primary bg-primary/5" 
                    : "border-border hover:border-primary/50"
                  }
                `}
              >
                <RadioGroupItem value="expanded" id="expanded" className="sr-only" />
                <div className="flex-1">
                  <p className="font-medium text-sm">Expanded Columns</p>
                  <p className="text-xs text-muted-foreground">
                    One column per matrix row. Best for Excel analysis.
                  </p>
                </div>
              </Label>
            </RadioGroup>
          </div>

          {/* Sort Order Selection */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Response Order</Label>
            <RadioGroup
              value={options.sortOrder}
              onValueChange={(value) =>
                setOptions({ ...options, sortOrder: value as "newest" | "oldest" })
              }
              className="space-y-2"
            >
              <Label
                htmlFor="newest"
                className={`
                  flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all
                  ${options.sortOrder === "newest" 
                    ? "border-primary bg-primary/5" 
                    : "border-border hover:border-primary/50"
                  }
                `}
              >
                <RadioGroupItem value="newest" id="newest" className="sr-only" />
                <div className="flex-1">
                  <p className="font-medium text-sm">Newest First</p>
                  <p className="text-xs text-muted-foreground">
                    Most recent responses at the top of the spreadsheet.
                  </p>
                </div>
              </Label>
              
              <Label
                htmlFor="oldest"
                className={`
                  flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all
                  ${options.sortOrder === "oldest" 
                    ? "border-primary bg-primary/5" 
                    : "border-border hover:border-primary/50"
                  }
                `}
              >
                <RadioGroupItem value="oldest" id="oldest" className="sr-only" />
                <div className="flex-1">
                  <p className="font-medium text-sm">Oldest First</p>
                  <p className="text-xs text-muted-foreground">
                    Earliest responses at the top of the spreadsheet.
                  </p>
                </div>
              </Label>
            </RadioGroup>
          </div>

          {/* Answer Format Selection */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Answer Format</Label>
            <p className="text-xs text-muted-foreground">
              For questions with option codes
            </p>
            <RadioGroup
              value={options.answerFormat}
              onValueChange={(value) =>
                setOptions({ ...options, answerFormat: value as AnswerExportFormat })
              }
              className="space-y-2"
            >
              <Label
                htmlFor="text_only"
                className={`
                  flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all
                  ${options.answerFormat === "text_only" 
                    ? "border-primary bg-primary/5" 
                    : "border-border hover:border-primary/50"
                  }
                `}
              >
                <RadioGroupItem value="text_only" id="text_only" className="sr-only" />
                <div className="flex-1">
                  <p className="font-medium text-sm">Text only</p>
                  <p className="text-xs text-muted-foreground">
                    Export answer labels (e.g., "To relax / de-stress")
                  </p>
                </div>
              </Label>
              
              <Label
                htmlFor="code_only"
                className={`
                  flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all
                  ${options.answerFormat === "code_only" 
                    ? "border-primary bg-primary/5" 
                    : "border-border hover:border-primary/50"
                  }
                `}
              >
                <RadioGroupItem value="code_only" id="code_only" className="sr-only" />
                <div className="flex-1">
                  <p className="font-medium text-sm">Code only</p>
                  <p className="text-xs text-muted-foreground">
                    Export option codes (e.g., "1", "A")
                  </p>
                </div>
              </Label>
              
              <Label
                htmlFor="code_and_text"
                className={`
                  flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all
                  ${options.answerFormat === "code_and_text" 
                    ? "border-primary bg-primary/5" 
                    : "border-border hover:border-primary/50"
                  }
                `}
              >
                <RadioGroupItem value="code_and_text" id="code_and_text" className="sr-only" />
                <div className="flex-1">
                  <p className="font-medium text-sm">Code and text</p>
                  <p className="text-xs text-muted-foreground">
                    Export both (e.g., "1. To relax / de-stress")
                  </p>
                </div>
              </Label>
            </RadioGroup>
          </div>

          {/* Additional Options */}
          <div className="space-y-3">
            <Label className="text-sm font-medium flex items-center gap-2">
              <Settings2 className="h-4 w-4" />
              Options
            </Label>
            <Card className="bg-muted/50">
              <CardContent className="p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="timestamps" className="text-sm cursor-pointer">
                    Include timestamps
                    <p className="text-xs text-muted-foreground font-normal">Start and completion times</p>
                  </Label>
                  <Checkbox
                    id="timestamps"
                    checked={options.includeTimestamps}
                    onCheckedChange={(checked) =>
                      setOptions({ ...options, includeTimestamps: checked as boolean })
                    }
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <Label htmlFor="metadata" className="text-sm cursor-pointer">
                    Include metadata
                    <p className="text-xs text-muted-foreground font-normal">Browser, device, location</p>
                  </Label>
                  <Checkbox
                    id="metadata"
                    checked={options.includeMetadata}
                    onCheckedChange={(checked) =>
                      setOptions({ ...options, includeMetadata: checked as boolean })
                    }
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        </div>

        <DialogFooter className="flex-shrink-0 gap-2 sm:gap-0 pt-4 border-t">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            className="bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600 text-white gap-2"
          >
            <Download className="h-4 w-4" />
            Export
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
